import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useIsServiceDeskUser } from '../hooks/useIsServiceDeskUser';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { toast } from 'react-hot-toast';
import RichTextEditor from './RichTextEditor';
import DateTimePickerInput from './DateTimePickerInput';
import { dateToUTCNoon } from '../utils/dateHelpers';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface Group {
  id: string;
  name: string;
  description?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface RootCauseAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (rcaData: {
    assignmentGroupId: string | null;
    assignedToId: string | null;
    assessment2Date: string;
    assessment2Summary: string;
  }) => void;
  problemId: string;
  currentProblem?: any;
  onShowGroupChangeConfirmation?: (data: {
    newGroupId: string;
    newGroupName: string;
    pendingPayload: any;
  }) => void;
}

const RootCauseAnalysisModal: React.FC<RootCauseAnalysisModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  problemId: _problemId,
  currentProblem,
  onShowGroupChangeConfirmation
}) => {
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const { getToday } = useDateFormatter();
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [assignedUserId, setAssignedUserId] = useState<string>('');
  const [assessment2Date, setAssessment2Date] = useState<string>('');
  const [assessment2Summary, setAssessment2Summary] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [groupQuery, setGroupQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');

  // Fetch assignment groups (only type: assignment)
  useEffect(() => {
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await api.get('/groups/assignment-groups');
        const data = Array.isArray(response.data) ? response.data : response.data.groups || [];
        setGroups(data);
      } catch (error) {
        console.error('Error fetching groups:', error);
        toast.error('Failed to load assignment groups');
      } finally {
        setLoadingGroups(false);
      }
    };

    if (isOpen) {
      fetchGroups();
      // Set default date to today in user's timezone
      setAssessment2Date(getToday());
    }
  }, [isOpen, getToday]);

  // Fetch users based on selected assignment group
  useEffect(() => {
    const fetchUsers = async () => {
      if (!assignmentGroupId) {
        setUsers([]);
        setAssignedUserId('');
        return;
      }

      setLoadingUsers(true);
      try {
        const members = await fetchAssignmentGroupMembers(
          assignmentGroupId,
          currentProblem?.customerId || currentProblem?.Customer?.id || null
        );
        setUsers(members || []);
      } catch (error) {
        console.error('Error fetching group members:', error);
        toast.error('Failed to load group members');
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [assignmentGroupId, currentProblem?.customerId, currentProblem?.Customer?.id]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }
    if (!assignedUserId) {
      newErrors.assignedUserId = 'Assigned To is required';
    }
    if (!assessment2Date) {
      newErrors.assessment2Date = 'Assessment 2 Date is required';
    }
    if (!assessment2Summary.trim()) {
      newErrors.assessment2Summary = 'Assessment Summary is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Check if user is changing to a different group and will lose access
    const currentGroupId = currentProblem?.assignmentGroupId;
    const newGroupId = assignmentGroupId;
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    // Service Desk users can access all tickets, so skip the warning for them
    // If not a global access role, not a Service Desk user, and group is changing, check if user will lose access
    if (!isGlobalAccessRole && !isServiceDeskUser && currentGroupId !== newGroupId && onShowGroupChangeConfirmation) {
      // Check if user is member of new group
      const isUserInNewGroup = users?.some((member: any) => member.userId === user?.id);

      if (!isUserInNewGroup) {
        // Get the new group name for the confirmation modal
        const newGroup = groups.find((g: any) => g.id === newGroupId);

        // Show confirmation modal via callback
        const assessment2DateISO = dateToUTCNoon(assessment2Date);
        onShowGroupChangeConfirmation({
          newGroupId: newGroupId,
          newGroupName: newGroup?.name || 'Unknown Group',
          pendingPayload: {
            problemState: 'root_cause_analysis',
            assignmentGroupId,
            assignedToId: assignedUserId,
            assessment2Date: assessment2DateISO,
            assessment2Summary,
            ...(currentProblem?.problemDate && { problemDate: currentProblem.problemDate })
          }
        });

        // Close this modal
        handleClose();
        return;
      }
    }

    setSubmitting(true);
    try {
      await onConfirm({
        assignmentGroupId,
        assignedToId: assignedUserId,
        assessment2Date,
        assessment2Summary
      });
      toast.success('Problem moved to Root Cause Analysis state successfully');
      handleClose();
    } catch (error: any) {
      console.error('Error submitting:', error);
      toast.error(error.response?.data?.message || 'Failed to move problem to Root Cause Analysis');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setAssignmentGroupId('');
    setAssignedUserId('');
    setAssessment2Date('');
    setAssessment2Summary('');
    setUsers([]);
    setErrors({});
    setGroupQuery('');
    setUserQuery('');
    onClose();
  };

  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((group) =>
        group.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const filteredUsers = userQuery === ''
    ? users
    : users.filter((user) =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userQuery.toLowerCase())
      );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Root Cause Analysis
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-4">
                  {/* Assignment Group and Assigned To - Same row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="assignmentGroup" className="block text-sm font-medium text-gray-700 mb-1">
                        Assignment Group <span className="text-red-500">*</span>
                      </label>
                      <Combobox
                        value={assignmentGroupId}
                        onChange={(value: string | null) => {
                          setAssignmentGroupId(value || '');
                          setAssignedUserId('');
                          setErrors({ ...errors, assignmentGroupId: '' });
                        }}
                        disabled={loadingGroups}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 ${
                              errors.assignmentGroupId
                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                            } disabled:bg-gray-100`}
                            displayValue={(groupId: string) => {
                              const group = groups.find((g) => g.id === groupId);
                              return group ? group.name : '';
                            }}
                            onChange={(event) => setGroupQuery(event.target.value)}
                            placeholder={loadingGroups ? 'Loading...' : 'Search groups...'}
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setGroupQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {filteredGroups.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No groups found.
                                </div>
                              ) : (
                                filteredGroups.map((group) => (
                                  <Combobox.Option
                                    key={group.id}
                                    value={group.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {group.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {errors.assignmentGroupId && (
                        <p className="mt-1 text-xs text-red-600">{errors.assignmentGroupId}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To <span className="text-red-500">*</span>
                      </label>
                      <Combobox
                        value={assignedUserId}
                        onChange={(value: string | null) => {
                          setAssignedUserId(value || '');
                          setErrors({ ...errors, assignedUserId: '' });
                        }}
                        disabled={!assignmentGroupId || loadingUsers}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 ${
                              errors.assignedUserId
                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                            } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                            displayValue={(userId: string) => {
                              const user = users.find((u) => u.id === userId);
                              return user ? `${user.firstName} ${user.lastName}` : '';
                            }}
                            onChange={(event) => setUserQuery(event.target.value)}
                            placeholder={
                              !assignmentGroupId
                                ? 'Select group first'
                                : loadingUsers
                                ? 'Loading...'
                                : 'Search members...'
                            }
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setUserQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {filteredUsers.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No users found.
                                </div>
                              ) : (
                                filteredUsers.map((user) => (
                                  <Combobox.Option
                                    key={user.id}
                                    value={user.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {user.firstName} {user.lastName}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {errors.assignedUserId && (
                        <p className="mt-1 text-xs text-red-600">{errors.assignedUserId}</p>
                      )}
                      {!assignmentGroupId && (
                        <p className="mt-1 text-xs text-gray-500">Select an assignment group first</p>
                      )}
                    </div>
                  </div>

                  {/* Assessment 2 Date - Half width */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="assessment2Date" className="block text-sm font-medium text-gray-700 mb-1">
                        Assessment 2 Date <span className="text-red-500">*</span>
                      </label>
                      <DateTimePickerInput
                        id="assessment2Date"
                        value={assessment2Date}
                        onChange={(value) => {
                          setAssessment2Date(value);
                          setErrors({ ...errors, assessment2Date: '' });
                        }}
                        max={getToday()}
                        showTimeSelect={false}
                        error={!!errors.assessment2Date}
                      />
                      {errors.assessment2Date && (
                        <p className="mt-1 text-xs text-red-600">{errors.assessment2Date}</p>
                      )}
                    </div>
                  </div>

                  {/* Assessment Summary - Full width */}
                  <div>
                    <label htmlFor="assessment2Summary" className="block text-sm font-medium text-gray-700 mb-1">
                      Assessment Summary <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={assessment2Summary}
                      onChange={(value) => {
                        setAssessment2Summary(value);
                        setErrors({ ...errors, assessment2Summary: '' });
                      }}
                      placeholder="Enter the assessment summary including root cause analysis findings..."
                      minHeight="120px"
                    />
                    {errors.assessment2Summary && (
                      <p className="mt-1 text-xs text-red-600">{errors.assessment2Summary}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default RootCauseAnalysisModal;
